import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  DocumentTextIcon, 
  ChevronDownIcon, 
  ChevronUpIcon,
  ArrowTopRightOnSquareIcon,
  ClipboardIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { getSourceIcon } from '../../config/sourceIcons';

const SourceCitations = ({ sources, sourceRefs }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedSource, setSelectedSource] = useState(null);
  const [linksCopied, setLinksCopied] = useState(false);

  if (!sources || sources.length === 0) return null;

  const displayedSources = isExpanded ? sources : sources.slice(0, 3);



  const truncateText = (text, maxLength = 100) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const handleSourceClick = (source) => {
    if (source.url) {
      window.open(source.url, '_blank', 'noopener,noreferrer');
    } else {
      setSelectedSource(selectedSource === source.id ? null : source.id);
    }
  };

  const copyAllLinks = async () => {
    try {
      // Filter sources that have URLs and format them nicely
      const linksWithTitles = sources
        .filter(source => source.url)
        .map(source => `${source.title || 'Untitled'}: ${source.url}`)
        .join('\n');
      
      if (linksWithTitles) {
        await navigator.clipboard.writeText(linksWithTitles);
        setLinksCopied(true);
        setTimeout(() => setLinksCopied(false), 2000);
      }
    } catch (error) {
      console.error('Failed to copy links to clipboard:', error);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gray-800/30 backdrop-blur-sm rounded-2xl p-4 border border-gray-700/50"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-sm font-semibold text-gray-200 flex items-center">
          <DocumentTextIcon className="w-4 h-4 mr-2 text-primary" />
          Sources ({sources.length})
        </h4>
        <div className="flex items-center gap-2">
          {/* Copy Links Button */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={copyAllLinks}
            className="text-xs text-gray-400 hover:text-white flex items-center transition-colors rounded-lg px-2 py-1 hover:bg-gray-700/50"
            title="Copy all source links"
          >
            {linksCopied ? (
              <>
                <CheckIcon className="w-3 h-3 mr-1 text-green-400" />
                Copied
              </>
            ) : (
              <>
                <ClipboardIcon className="w-3 h-3 mr-1" />
                Copy Links
              </>
            )}
          </motion.button>
          
          {/* Expand/Collapse Button */}
          {sources.length > 3 && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-xs text-primary hover:text-primary-light flex items-center transition-colors rounded-lg px-2 py-1 hover:bg-gray-700/50"
            >
              {isExpanded ? (
                <>
                  Less <ChevronUpIcon className="w-3 h-3 ml-1" />
                </>
              ) : (
                <>
                  All <ChevronDownIcon className="w-3 h-3 ml-1" />
                </>
              )}
            </motion.button>
          )}
        </div>
      </div>

      {/* Sources List */}
      <div className="space-y-3">
        <AnimatePresence>
          {displayedSources.map((source, index) => (
            <motion.div
              key={source.id || index}
              ref={(el) => sourceRefs && (sourceRefs.current[source.id] = el)}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ delay: index * 0.05 }}
              className={`group cursor-pointer transition-all duration-200 ${
                selectedSource === source.id ? 'bg-gray-700/50 shadow-md' : 'hover:bg-gray-700/30'
              } rounded-lg p-3 border border-transparent hover:border-gray-600/50`}
              onClick={() => handleSourceClick(source)}
            >
              {/* Source Header */}
              <div className="flex items-start gap-3 mb-2">
                <div className="flex-shrink-0 mt-0.5 text-gray-400 group-hover:text-primary transition-colors">
                  {getSourceIcon(source)}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h5 className="text-sm font-medium text-white group-hover:text-primary transition-colors leading-tight">
                      {truncateText(source.title || `Source ${index + 1}`, 40)}
                    </h5>
                      {source.url && (
                      <ArrowTopRightOnSquareIcon className="w-3 h-3 text-gray-400 group-hover:text-primary transition-colors flex-shrink-0" />
                      )}
                    </div>
                </div>
              </div>

              {/* Metrics Row */}
              <div className="flex items-center gap-2 mb-2">
                {/* Source Type Badge */}
                {source.type && (() => {
                  // Determine source type and styling
                  const isOpenverse = source.url && (
                    source.url.includes('openverse.fyi') || 
                    source.url.includes('openverse.org')
                  ) || source.content_source === 'openverse_api' || source.type === 'openverse_resource';
                  
                  const isOpenOrgResource = source.type === 'openorg_resource';
                  
                  const isNewsletter = source.type === 'newsletter' || 
                    (source.metadata && source.metadata.category === 'newsletter') ||
                    (source.url && (source.url.includes('substack.com') || 
                                    source.url.includes('beehiiv.com') || 
                                    source.url.includes('newsletter')));
                  
                  let displayType, className;
                  
                  if (isOpenverse) {
                    displayType = 'Openverse';
                    className = 'bg-gradient-to-r from-purple-500/15 to-indigo-500/15 text-purple-300 border border-purple-400/20';
                  } else if (isOpenOrgResource) {
                    displayType = 'Open Org';
                    className = 'bg-gradient-to-r from-orange-500/15 to-red-500/15 text-orange-300 border border-orange-400/20';
                  } else if (isNewsletter) {
                    displayType = 'Newsletter';
                    className = 'bg-gradient-to-r from-yellow-500/15 to-orange-500/15 text-yellow-300 border border-yellow-400/20';
                  } else {
                    displayType = source.type.charAt(0).toUpperCase() + source.type.slice(1);
                    className = 'bg-gray-500/15 text-gray-300';
                  }
                  
                  return (
                    <span className={`text-xs px-2 py-1 rounded-md font-medium ${className}`}>
                      {displayType}
                    </span>
                  );
                })()}
                
                {source.confidence && (
                  <span className="text-xs bg-primary/15 text-primary px-2 py-1 rounded-md font-medium">
                    {Math.round(source.confidence * 100)}%
                  </span>
                )}
                {source.matchType && (
                  <span className={`text-xs px-2 py-1 rounded-md font-medium ${
                    source.matchType === 'vector' 
                      ? 'bg-purple-500/15 text-purple-300' 
                      : source.matchType === 'hybrid'
                      ? 'bg-green-500/15 text-green-300'
                      : 'bg-blue-500/15 text-blue-300'
                  }`}>
                    {source.matchType}
                  </span>
                )}
                  </div>
                  
              {/* Excerpt */}
                  {source.excerpt && (
                <p className="text-xs text-gray-400 leading-relaxed mb-2">
                  {truncateText(source.excerpt, 120)}
                    </p>
                  )}
                  
              {/* Metadata */}
                  {source.metadata && (
                <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                      {source.metadata.section && (
                        <span className="flex items-center">
                      <span className="w-1 h-1 bg-gray-500 rounded-full mr-1.5"></span>
                      {truncateText(source.metadata.section, 20)}
                        </span>
                      )}
                      {source.metadata.page && (
                        <span className="flex items-center">
                      <span className="w-1 h-1 bg-gray-500 rounded-full mr-1.5"></span>
                          Page {source.metadata.page}
                        </span>
                      )}
                      {source.metadata.lastModified && (
                        <span className="flex items-center">
                      <span className="w-1 h-1 bg-gray-500 rounded-full mr-1.5"></span>
                      {new Date(source.metadata.lastModified).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                  )}
                </div>
              )}

              {/* Expanded content */}
              <AnimatePresence>
                {selectedSource === source.id && source.fullContent && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-3 pt-3 border-t border-gray-600/30"
                  >
                    <p className="text-xs text-gray-300 leading-relaxed">
                      {source.fullContent}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Document Type Summary */}
      <div className="mt-4 pt-3 border-t border-gray-700/30">
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-3 flex-wrap">
            {(() => {
              // Count and categorize source types
              const typeCounts = sources.reduce((acc, source) => {
                let type = 'other';
                
                // Determine source type
                const isOpenverse = source.url && (
                  source.url.includes('openverse.fyi') || 
                  source.url.includes('openverse.org')
                ) || source.content_source === 'openverse_api' || source.type === 'openverse_resource';
                
                const isNewsletter = source.type === 'newsletter' || 
                  (source.metadata && source.metadata.category === 'newsletter') ||
                  (source.url && (source.url.includes('substack.com') || 
                                  source.url.includes('beehiiv.com') || 
                                  source.url.includes('newsletter')));
                
                if (source.type === 'playbook') {
                  type = 'playbook';
                } else if (isOpenverse) {
                  type = 'openverse';
                } else if (isNewsletter) {
                  type = 'newsletter';
                } else if (source.type === 'document') {
                  type = 'document';
                } else if (source.type === 'external') {
                  type = 'external';
                } else if (source.type) {
                  type = source.type;
                }
                
                acc[type] = (acc[type] || 0) + 1;
                return acc;
              }, {});

              // Define colors and labels for each type
              const typeConfig = {
                playbook: { label: 'playbooks', color: 'bg-indigo-500', textColor: 'text-indigo-300' },
                openverse: { label: 'openverse', color: 'bg-purple-500', textColor: 'text-purple-300' },
                newsletter: { label: 'newsletters', color: 'bg-yellow-500', textColor: 'text-yellow-300' },
                document: { label: 'docs', color: 'bg-gray-500', textColor: 'text-gray-300' },
                external: { label: 'external', color: 'bg-blue-500', textColor: 'text-blue-300' },
                other: { label: 'other', color: 'bg-gray-500', textColor: 'text-gray-300' }
              };

              return Object.entries(typeCounts).map(([type, count]) => {
                const config = typeConfig[type] || typeConfig.other;
                return (
                  <div key={type} className="flex items-center gap-1.5">
                    <div className={`w-2 h-2 ${config.color} rounded-full`}></div>
                    <span className={`${config.textColor} font-medium`}>
                      {count} {config.label}
                    </span>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default SourceCitations;